import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { List } from '../../models/list.model';
import { ApplicationDto } from '../../models/application.model';
import { AppDocumentVersion, FormModel } from '../../models/forms.model';
import { ApplicationTypeDto } from '../../models/application-type.model';
import { ConfigService } from 'src/app/core/services/config-service';
import { EnvironmentType } from '../../models/enums/environment-type';

@Injectable()
export class ApplicationService {
  constructor(private http: HttpClient) {}

  configService = inject(ConfigService);

  load(params: {
    page: number;
    pageSize: number;
    sort: string;
    sortDirection: string;
    filters: string[];
  }): Observable<List<ApplicationDto>> {
    const url =
      this.configService.readConfig().baseUrlInfrastructure +
      `/application/GetApplicationWithPagination` +
      `?PageSize=${params.pageSize}` +
      `&PageNumber=${params.page}` +
      `&Sort=${params.sort}` +
      `&SortDirection=${params.sortDirection}` +
      `&Filter=${params.filters.join('&Filter=')}`;
    return this.http.get<List<ApplicationDto>>(url);
  }

  loadApplicationTypes(params: {
    page: number;
    pageSize: number;
    sort: string;
    sortDirection: string;
    filters: string[];
  }): Observable<List<ApplicationTypeDto>> {
    const url =
      this.configService.readConfig().baseUrlInfrastructure +
      `/applicationType/GetApplicationTypeWithPagination` +
      `?PageSize=${params.pageSize}` +
      `&PageNumber=${params.page}` +
      `&Sort=${params.sort}` +
      `&SortDirection=${params.sortDirection}` +
      `&Filter=${params.filters.join('&Filter=')}`;
    return this.http.get<List<ApplicationTypeDto>>(url);
  }

  getApplicationType(id: number): Observable<ApplicationTypeDto> {
    const url =
      this.configService.readConfig().baseUrlInfrastructure +
      '/applicationType/' +
      id;
    return this.http.get<ApplicationTypeDto>(url);
  }

  createApplicationType(
    applicationType: ApplicationTypeDto
  ): Observable<number> {
    const createUrl =
      this.configService.readConfig().baseUrlInfrastructure +
      '/applicationType';
    return this.http.post<number>(createUrl, applicationType);
  }

  updateApplicationType(
    applicationType: ApplicationTypeDto
  ): Observable<ApplicationTypeDto> {
    const url =
      this.configService.readConfig().baseUrlInfrastructure +
      '/applicationType';
    return this.http.put<ApplicationTypeDto>(url, applicationType);
  }

  removeApplicationType(applicationTypeId: number): Observable<any> {
    const url =
      this.configService.readConfig().baseUrlInfrastructure +
      '/applicationType/' +
      applicationTypeId;
    return this.http.delete(url);
  }

  remove(applicationUuid: string): Observable<any> {
    const url =
      this.configService.readConfig().baseUrlInfrastructure +
      '/application/' +
      applicationUuid;
    return this.http.delete(url);
  }

  duplicate(payload: {
    uuid?: string;
    id?: number;
    env?: string;
  }): Observable<{ data: string }> {
    const url =
      this.configService.readConfig().baseUrlInfrastructure +
      '/Application/Duplicate';
    return this.http.post<{ data: string }>(url, payload);
  }

  saveConfigByApplicationId(
    applicationUuid: string,
    config: FormModel
  ): Observable<any> {
    const requestModel = {
      UUID: applicationUuid,
      Env: EnvironmentType.Config,
      Name: config.generalData.name,
      Configuration: JSON.stringify(config),
      AppTypeId: config.generalData.applicationType,
    };

    let url = `${
      this.configService.readConfig().baseUrlInfrastructure
    }/application`;
    if (applicationUuid) {
      url += '/' + applicationUuid;
    } else {
      requestModel.UUID = '00000000-0000-0000-0000-000000000000';
    }
    return this.http.post(url, requestModel);
  }

  uploadDocument(document: AppDocumentVersion): Observable<number> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    const url =
      this.configService.readConfig().baseUrlInfrastructure +
      '/application/UploadDocument';
    return this.http.post<number>(url, document, { headers });
  }

  getApplicationDocumentVersions(
    documentId: string
  ): Observable<AppDocumentVersion[]> {
    const url =
      this.configService.readConfig().baseUrlInfrastructure +
      '/application/GetApplicationDocumentVersions/' +
      documentId;
    return this.http.get<AppDocumentVersion[]>(url);
  }

  getApplicationDocumentVersion(
    versionId: number
  ): Observable<AppDocumentVersion> {
    const url =
      this.configService.readConfig().baseUrlInfrastructure +
      '/application/GetApplicationDocumentVersion/' +
      versionId;
    return this.http.get<AppDocumentVersion>(url);
  }

  getAvailableParentApplications(): Observable<{ uuid: string; name: string; applicationTypeId: number }[]> {
    const url = this.configService.readConfig().baseUrlInfrastructure + '/application/GetAvailableParents';
    return this.http.get<{ uuid: string; name: string; applicationTypeId: number }[]>(url);
  }

  getApplicationFields(applicationUuid: string): Observable<{ id: string; fieldName: string }[]> {
    const url = this.configService.readConfig().baseUrlInfrastructure + `/application/GetFields/${applicationUuid}`;
    return this.http.get<{ id: string; fieldName: string }[]>(url);
  }

  getChildApplicationConfigs(parentUuid: string): Observable<{ uuid: string; name: string; applicationTypeId: number }[]> {
    const url = this.configService.readConfig().baseUrlInfrastructure + `/application/GetChildApplications/${parentUuid}`;
    return this.http.get<{ uuid: string; name: string; applicationTypeId: number }[]>(url);
  }
}
